import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { meApi } from '@/api/client';
import type { Surface } from '@/surfaces';

/**
 * Shown in place of a surface's page when the acting org doesn't have the
 * surface (#1160): its URL was bookmarked, linked or typed. Not a 404 (the
 * page exists) and not the API's raw 403: the org can get it, from Jeket.
 */
export default function SurfaceNotEnabled({ surface, orgId }: { surface: Surface; orgId: string | null | undefined }) {
  const { data: organizations } = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => meApi.organizations(),
    staleTime: 60_000,
  });
  const orgName = organizations?.find((o) => o.id === orgId)?.name ?? 'your organization';
  const Icon = surface.icon;

  return (
    <div className="flex items-center justify-center py-16" data-testid="surface-not-enabled">
      <div className="w-full max-w-md bg-surface border border-gold/20 rounded-2xl p-8 shadow-jeket text-center">
        <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mx-auto mb-4">
          <Icon size={24} className="text-gold" />
        </div>
        <h2 className="text-xl font-semibold text-silver-bright flex items-center justify-center gap-2">
          <Lock size={16} className="text-silver-muted" aria-hidden />
          {surface.label} is not enabled for {orgName}
        </h2>
        <p className="text-sm text-silver-muted mt-3">
          Contact Jeket to turn {surface.label} on for your organization.
        </p>
        <Link
          to="/home"
          className="inline-block mt-6 py-2 px-4 rounded-lg text-sm font-semibold bg-gold hover:bg-gold-light text-surface-deep"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
